import { fetchTableData } from "../../../api"
import { compactSmall } from "../../../helpers/formatters"
import { useI18n } from "providers/I18n"
import { useQuery } from "react-query"
import { useRevenueParams } from "../../../hooks/useRevenueParams"
import { useSortAndOrder } from "../../../hooks/useSortAndOrder"
import { useMemo, useState } from "react"
import { RootLayout } from "./RootLayout"
import { TableTitle } from "./TableTitle"
import { ContentLayout } from "../../SharedComponents/ContentLayout"
import { FooterLayout } from "../../SharedComponents/FooterLayout"
import { FooterTable } from "../../SharedComponents/FooterTable"
import { mapViewGridFooter } from "../../../styles"
import {
  brandsRow,
  clientsRow,
  marketsRow,
} from "../../../tablesInfoData/tableRows"
import { BaseTable } from "../../SharedComponents/BaseTable"

interface ClientBaseProps {
  type:
    | "markets"
    | "clientbrands"
    | "marketsummary"
    | "brandbreakdown"
    | "top20clients"
}

export function BaseMapTable({ type }: ClientBaseProps) {
  const { t } = useI18n()
  const { search, clientParam, brandParam, regionParam, isStream } =
    useRevenueParams()
  const [error, setError] = useState<boolean>(false)

  const url = useMemo(() => {
    if (type === "marketsummary") {
      return `Client/${clientParam}/Markets`
    } else if (type === "clientbrands") {
      return `client/${clientParam}/brands`
    } else if (type === "markets") {
      return `brand/${brandParam}/Markets`
    } else if (type === "brandbreakdown") {
      return `region/${regionParam}/brands`
    } else if (type === "top20clients" && isStream) {
      return "client/summary/GetTop20Clients"
    } else if (type === "top20clients" && brandParam === "GCF") {
      return "client/GCF/GetTop20Clients"
    } else if (type === "top20clients" && brandParam && brandParam !== "All") {
      return `brand/${brandParam}/GetTop20Clients`
    } else {
      return `region/${regionParam}/GetTop20Clients`
    }
  }, [brandParam, clientParam, isStream, regionParam, type])

  const key = useMemo(() => {
    if (type === "marketsummary") {
      return "markets"
    } else if (type === "clientbrands") {
      return "clientbrands"
    } else if (type === "markets") {
      return "brandMarkets"
    } else if (type === "brandbreakdown") {
      return "brands"
    } else if (type === "top20clients" && (isStream || brandParam === "GCF")) {
      return "streammarkets"
    } else if (type === "top20clients" && brandParam && brandParam !== "All") {
      return "brandTop20Clients"
    }
    return "top20clients"
  }, [brandParam, isStream, type])

  const { data, isLoading, updatedAt } = useQuery(
    [key, url, search],
    fetchTableData,
    {
      enabled: !!search,
      onSuccess: () => (!!error ? setError(false) : null),
      onError: () => setError(true),
    },
  )
  const { sortAndOrder, onSort, sorted } = useSortAndOrder(
    type === "marketsummary" || type === "markets"
      ? data?.markets
      : type === "top20clients"
        ? data?.clients
        : data?.brands,
    updatedAt,
  )

  const footer = [
    { value: t("revenue.totalrevenue"), align: "left" },
    {
      value: `${
        data?.totalRevenue ? "$" + compactSmall(data?.totalRevenue) : "N/A"
      }`,
      align: "center",
    },
    {
      value: `${
        data?.totalGrowth ? compactSmall(data?.totalGrowth) + "%" : "N/A"
      }`,
      align: "center",
    },
    {
      value: `${
        data?.totalMargin ? compactSmall(data?.totalMargin) + "%" : "N/A"
      }`,
      align: "center",
    },
  ]

  return (
    <RootLayout>
      <TableTitle
        title={
          type === "marketsummary"
            ? t("revenue.marketsummary")
            : type === "markets"
              ? t("revenue.markets")
              : type === "top20clients"
                ? t("revenue.top20clients")
                : t("revenue.revenuestreamsbreakdown")
        }
      />
      <ContentLayout maxHeight="416px">
        <BaseTable
          hasError={error}
          rows={
            type === "marketsummary" || type === "markets"
              ? marketsRow(t)
              : type === "top20clients"
                ? clientsRow(t)
                : brandsRow(t)
          }
          data={sorted || []}
          isLoading={isLoading}
          onSort={onSort}
          selectedSortField={sortAndOrder.sort}
          selectedSortOrder={sortAndOrder.order}
          columnWidth={540 - 48}
          gap={0}
          cellsPerColumn={4}
        />
      </ContentLayout>
      <FooterLayout customClass={mapViewGridFooter.root}>
        <FooterTable
          footer={footer}
          columnWidth={540 - 48}
          gap={0}
          columnsLength={4}
        />
      </FooterLayout>
    </RootLayout>
  )
}
